import type { AuthUserEntityStore } from '@/shared/models';
import { AuthEncryptionService } from './authEncryptionService';

/**
 * Servicio de persistencia de datos de autenticación con cifrado
 * Cumple con SRP: manejo seguro de datos de sesión en localStorage
 */
class AuthPersistenceService {
  /**
   * Carga datos de autenticación desde localStorage con descifrado y validación
   * @param storageKey - Clave del localStorage donde se almacena el estado
   */
  loadAuthData(storageKey: string): { state: AuthUserEntityStore } | null {
    const dataStorage = localStorage.getItem(storageKey);
    if (!dataStorage) return null;

    try {
      const parsed = JSON.parse(dataStorage);
      const encryptedValuesObject: AuthUserEntityStore = parsed.state;

      const decryptedValues = AuthEncryptionService.decrypt(
        encryptedValuesObject,
      );

      const sanitizedValues = this.sanitizeAuthStateBasic(decryptedValues);

      if (!this.validateAuthStateBasic(sanitizedValues)) {
        return null;
      }

      return { state: sanitizedValues };
    } catch (error) {
      console.error('Failed to load auth state from storage:', error);
      return null;
    }
  }

  /**
   * Sanitiza y normaliza datos de autenticación para seguridad
   * @param authState - Estado de autenticación a sanitizar
   */
  private sanitizeAuthStateBasic(
    authState: AuthUserEntityStore,
  ): AuthUserEntityStore {
    return {
      id: authState.id,
      name: authState.name?.trim() || null,
      email: authState.email?.toLowerCase().trim() || null,
      run: authState.run?.trim() || null,
      role: authState.role?.trim() || null,
      token: authState.token?.trim() || null,
      es_admin: authState.es_admin || null,
      ultimo_acceso: authState.ultimo_acceso?.trim() || null,
      cod_tipo_perfil: authState.cod_tipo_perfil || null,
      desc_tipo_perfil: authState.desc_tipo_perfil?.trim() || null,
    };
  }

  /**
   * Valida que el estado de autenticación contenga los campos críticos mínimos
   * @param authState - Estado a validar
   */
  private validateAuthStateBasic(authState: AuthUserEntityStore): boolean {
    return !!(
      authState.token &&
      authState.role &&
      authState.email &&
      authState.name
    );
  }

  /**
   * Guarda datos de autenticación en localStorage con cifrado
   * @param storageKey - Clave del localStorage donde persistir el estado
   * @param authData - Datos de autenticación a guardar
   */
  saveAuthData(
    storageKey: string,
    authData: { state: AuthUserEntityStore },
  ): void {
    const sanitizedData = this.sanitizeAuthStateBasic(authData.state);

    const encryptedValues = AuthEncryptionService.encrypt(sanitizedData);

    localStorage.setItem(
      storageKey,
      JSON.stringify({ state: encryptedValues }),
    );
  }

  /**
   * Elimina datos de autenticación específicos del localStorage
   * @param storageKey - Clave del localStorage a eliminar
   */
  removeAuthData(storageKey: string): void {
    localStorage.removeItem(storageKey);
  }

  /**
   * Limpia completamente todos los datos de la aplicación en localStorage
   */
  clearAllAppData(): void {
    localStorage.removeItem('@app');
    localStorage.removeItem('@app/auth');
    localStorage.removeItem('@app/users');
    localStorage.removeItem('@app/student');
    localStorage.removeItem('@app/filter');
    localStorage.removeItem('@app/home');
    localStorage.removeItem('@app/career-layer');
    localStorage.removeItem('@app/center-layer');
    localStorage.removeItem('@app/academic-risk');
    localStorage.removeItem('@app/notifications');
  }
}

export const authPersistenceService = new AuthPersistenceService();
